import { addIpEnrichment, bulkGetIpEnrichments, getIpEnrichment } from './storage.js';
import { COUNTRY_COORDS, COUNTRY_NAMES } from './util.js';

const FIELDS = 'status,lat,lon,city,regionName,country,countryCode,isp,org,as,asname,query';
const BATCH_SIZE = 100;
const BATCH_INTERVAL_MS = 2500;
const FALLBACK_CONCURRENCY = 5;
const FALLBACK_INTERVAL_MS = 1500;

function isPrivateIP(ip) {
  const parts = ip.split('.').map(Number);
  if (parts.length !== 4) return false;
  if (parts[0] === 10) return true;
  if (parts[0] === 172 && parts[1] >= 16 && parts[1] <= 31) return true;
  if (parts[0] === 192 && parts[1] === 168) return true;
  if (parts[0] === 127) return true;
  if (parts[0] === 169 && parts[1] === 254) return true;
  return false;
}

function makeEnrichment(ip, data) {
  if (data && data.status === 'success') {
    return {
      ip: data.query,
      lat: data.lat,
      lon: data.lon,
      city: data.city,
      region: data.regionName,
      country: data.country,
      countryCode: data.countryCode,
      isp: data.isp,
      org: data.org,
      asn: data.as,
      asnName: data.asname,
      cachedAt: new Date().toISOString(),
    };
  }
  return {
    ip,
    lat: null, lon: null, city: null, region: null,
    country: null, countryCode: null, isp: null, org: null,
    asn: null, asnName: null,
    cachedAt: new Date().toISOString(),
    lookupFailed: true,
  };
}

async function batchFetchDirect(batch) {
  const res = await fetch(`http://ip-api.com/batch?fields=${FIELDS}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(batch),
  });
  if (!res.ok) throw new Error(`Direct batch failed: ${res.status}`);
  return res.json();
}

async function batchFetchViaProxy(batch) {
  const res = await fetch('/api/geoip', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ips: batch }),
  });
  if (!res.ok) throw new Error(`Proxy batch failed: ${res.status}`);
  const data = await res.json();
  return data.results || data;
}

let lastFallbackCall = 0;

async function fallbackFetch(ip) {
  const now = Date.now();
  const wait = Math.max(0, FALLBACK_INTERVAL_MS - (now - lastFallbackCall));
  if (wait > 0) await new Promise(r => setTimeout(r, wait));
  const res = await fetch(`https://ip-api.com/json/${ip}?fields=${FIELDS}`);
  lastFallbackCall = Date.now();
  return res.json();
}

async function processInBatches(strategyFn, needLookup, onProgress, startCount, total) {
  const results = new Array(needLookup.length);
  let resolvedCount = startCount;

  for (let i = 0; i < needLookup.length; i += BATCH_SIZE) {
    const batch = needLookup.slice(i, i + BATCH_SIZE);
    const batchResults = await strategyFn(batch);

    const promises = batchResults.map(async (data, j) => {
      const ip = batch[j];
      const enrichment = makeEnrichment(ip, data);
      await addIpEnrichment(enrichment);
      results[i + j] = enrichment;
      resolvedCount++;
      if (onProgress) onProgress(resolvedCount, total);
    });
    await Promise.all(promises);
  }

  return results;
}

async function processIndividualFallback(needLookup, onProgress, startCount, total) {
  const results = new Array(needLookup.length);
  let idx = 0;
  let resolvedCount = startCount;

  async function worker() {
    while (true) {
      const i = idx++;
      if (i >= needLookup.length) return;
      const ip = needLookup[i];
      try {
        const data = await fallbackFetch(ip);
        const enrichment = makeEnrichment(ip, data);
        await addIpEnrichment(enrichment);
        results[i] = enrichment;
      } catch (e) {
        console.warn('[geoip] fallback failed for', ip, e.message);
        results[i] = null;
      }
      resolvedCount++;
      if (onProgress) onProgress(resolvedCount, total);
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(FALLBACK_CONCURRENCY, needLookup.length) }, worker)
  );
  return results;
}

export async function batchLookupIPs(ips, onProgress) {
  const deduped = [...new Set(ips)];
  const total = deduped.length;
  const results = new Array(total);
  let resolvedCount = 0;

  const entries = deduped.map((ip, idx) => {
    if (isPrivateIP(ip)) {
      return {
        idx,
        enrichment: { ip, lat: 0, lon: 0, city: 'Private', country: 'Private', countryCode: 'N/A', isp: 'Local Network', isPrivate: true },
      };
    }
    return { idx, ip };
  });

  // Resolve private IPs immediately
  for (const e of entries) {
    if (e.enrichment) {
      results[e.idx] = e.enrichment;
      resolvedCount++;
      if (onProgress) onProgress(resolvedCount, total);
    }
  }

  const publicIPs = entries.filter(e => e.ip).map(e => e.ip);

  const cachedMap = await bulkGetIpEnrichments(publicIPs);

  const needLookup = [];
  for (const e of entries) {
    if (!e.ip) continue;
    const cached = cachedMap[e.ip];
    if (cached) {
      results[e.idx] = cached;
      resolvedCount++;
      if (onProgress) onProgress(resolvedCount, total);
    } else {
      needLookup.push(e.ip);
    }
  }

  if (needLookup.length === 0) {
    return results.filter(Boolean);
  }

  let batchResults;

  try {
    batchResults = await processInBatches(
      batchFetchDirect, needLookup, onProgress, resolvedCount, total
    );
  } catch (err) {
    console.warn('[geoip] direct batch failed, trying proxy:', err.message);
    try {
      batchResults = await processInBatches(
        batchFetchViaProxy, needLookup, onProgress, resolvedCount, total
      );
    } catch (err2) {
      console.warn('[geoip] proxy batch failed, falling back to individual requests:', err2.message);
      batchResults = await processIndividualFallback(
        needLookup, onProgress, resolvedCount, total
      );
    }
  }

  // Use a Map for O(1) lookups instead of Array.find()
  const entriesByIP = new Map();
  for (const e of entries) {
    if (e.ip) entriesByIP.set(e.ip, e);
  }

  for (let i = 0; i < needLookup.length; i++) {
    const entry = entriesByIP.get(needLookup[i]);
    if (entry) {
      results[entry.idx] = batchResults[i] || null;
    }
  }

  // Return explicitly failed IPs with a lookupFailed flag instead of filtering them away
  return results.filter(r => r !== null);
}

export async function lookupSingleIP(ip) {
  if (isPrivateIP(ip)) {
    return {
      ip,
      lat: 0, lon: 0,
      city: 'Private', country: 'Private', countryCode: 'N/A',
      isp: 'Local Network', isPrivate: true,
    };
  }

  const cachedMap = await bulkGetIpEnrichments([ip]);
  if (cachedMap[ip]) return cachedMap[ip];

  try {
    const batchData = await batchFetchDirect([ip]);
    const data = batchData && batchData[0];
    if (data && data.status === 'success') {
      const enrichment = makeEnrichment(ip, data);
      await addIpEnrichment(enrichment);
      return enrichment;
    }
  } catch (err) {
    console.warn('[geoip] single lookup direct failed for', ip, err.message);
  }

  try {
    const data = await fallbackFetch(ip);
    if (data && data.status === 'success') {
      const enrichment = makeEnrichment(ip, data);
      await addIpEnrichment(enrichment);
      return enrichment;
    }
  } catch (err) {
    console.warn('[geoip] single lookup fallback failed for', ip, err.message);
  }

  return null;
}

export async function updateGeoFromWhois(ip, whoisData) {
  if (!whoisData || !whoisData.country) return null;

  const existing = await getIpEnrichment(ip);
  if (existing && existing.lat && !existing.fromWhois) return existing;

  const countryCode = whoisData.country.toUpperCase();
  const coords = COUNTRY_COORDS[countryCode] || { lat: null, lon: null };

  const enrichment = {
    ip,
    lat: coords.lat,
    lon: coords.lon,
    city: whoisData.city || '',
    region: '',
    country: whoisData.countryName || COUNTRY_NAMES[countryCode] || countryCode,
    countryCode,
    isp: whoisData.name || existing?.isp || '',
    org: whoisData.name || existing?.org || '',
    asn: existing?.asn || '',
    asnName: existing?.asnName || '',
    isPrivate: false,
    fromWhois: true,
    cachedAt: new Date().toISOString(),
  };

  await addIpEnrichment(enrichment);
  return enrichment;
}

export async function updateGeoFromShodan(ip, shodanData) {
  if (!shodanData || !shodanData.countryName) return null;

  const existing = await getIpEnrichment(ip);
  if (existing && existing.lat && !existing.fromWhois && !existing.fromShodan) return existing;

  const enrichment = {
    ip,
    lat: shodanData.latitude || null,
    lon: shodanData.longitude || null,
    city: shodanData.city || '',
    region: '',
    country: shodanData.countryName || '',
    countryCode: '',
    isp: shodanData.isp || existing?.isp || '',
    org: shodanData.org || existing?.org || '',
    asn: existing?.asn || '',
    asnName: existing?.asnName || '',
    isPrivate: false,
    fromShodan: true,
    cachedAt: new Date().toISOString(),
  };

  await addIpEnrichment(enrichment);
  return enrichment;
}

export function isPrivateIPCheck(ip) {
  return isPrivateIP(ip);
}
